import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DOMAINS, hasFunctionAccess } from '../permissionConfig';
import { AccessLockedModal } from './AccessLocked';

const IconSearch = (p) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);
const IconLock = (p) => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" {...p}>
        <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
);

// App.css has a global `input:focus { border-color/box-shadow/outline !important }`
// rule that beats any inline style — this class-based override wins on
// specificity + !important so the search input stays borderless on focus.
const CSS = `
.sfip-search-input:focus {
    border-color: transparent !important;
    box-shadow: none !important;
    background: transparent !important;
    outline: none !important;
}
`;

export default function PortalSearch({ user }) {
    const navigate = useNavigate();
    const [query, setQuery]     = useState('');
    const [open, setOpen]       = useState(false);
    const [lockedLabel, setLockedLabel] = useState(null);
    const wrapRef = useRef(null);

    const index = DOMAINS.filter(d => d.key !== 'warehouse').flatMap(d =>
        d.functions.map(f => ({
            domainLabel: d.label,
            domainKey:   d.key,
            label:       f.label,
            path:        f.path,
            locked:      !hasFunctionAccess(user, d.key, f.key),
        }))
    );

    const results = query.trim()
        ? index.filter(r => `${r.domainLabel} ${r.label}`.toLowerCase().includes(query.trim().toLowerCase()))
        : [];

    useEffect(() => {
        function onClickOutside(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    function selectResult(r) {
        if (r.locked) { setLockedLabel(`${r.domainLabel} — ${r.label}`); return; }
        setOpen(false); setQuery('');
        navigate(r.path);
    }

    return (
        <div ref={wrapRef} style={S.wrap}>
            <style>{CSS}</style>
            <div style={S.box}>
                <IconSearch style={{ color: '#9A90AE', flex: 'none' }} />
                <input
                    className="sfip-search-input"
                    value={query}
                    onChange={e => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    placeholder="Search pages & functions…"
                    style={S.input}
                />
            </div>

            {open && query.trim() && (
                <div style={S.dropdown}>
                    {results.length === 0 ? (
                        <div style={S.empty}>No matches for "{query}"</div>
                    ) : results.map(r => (
                        <div key={`${r.domainKey}-${r.path}`} style={S.result} onClick={() => selectResult(r)}
                            onMouseEnter={e => (e.currentTarget.style.background = '#FBFAFD')}
                            onMouseLeave={e => (e.currentTarget.style.background = '#fff')}>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ ...S.resultLabel, color: r.locked ? '#B7AFC6' : '#231C30' }}>{r.label}</div>
                                <div style={S.resultDomain}>{r.domainLabel}</div>
                            </div>
                            {r.locked && <IconLock style={{ color: '#C9C2D6', flex: 'none' }} />}
                        </div>
                    ))}
                </div>
            )}

            <AccessLockedModal open={!!lockedLabel} label={lockedLabel} onClose={() => setLockedLabel(null)} />
        </div>
    );
}

const S = {
    wrap: { position: 'relative', width: '100%' },
    box: {
        display: 'flex', alignItems: 'center', gap: 10, height: 44,
        padding: '0 16px', borderRadius: 11, background: '#FAF8FC',
        border: '1px solid #ECE7F2',
    },
    input: {
        border: 'none', outline: 'none', boxShadow: 'none', background: 'transparent', flex: 1,
        fontSize: 16, color: '#231C30', fontFamily: 'inherit', WebkitAppearance: 'none',
        forcedColorAdjust: 'none',
    },
    dropdown: {
        position: 'absolute', top: 46, left: 0, right: 0, zIndex: 500,
        background: '#fff', border: '1px solid #ECE7F2', borderRadius: 14,
        boxShadow: '0 16px 40px rgba(76,59,120,0.16)', overflow: 'hidden',
        maxHeight: 320, overflowY: 'auto',
    },
    empty: { padding: '16px 18px', fontSize: 13, color: '#9A90AE' },
    result: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        padding: '11px 16px', cursor: 'pointer', borderBottom: '1px solid #F5F2FA',
    },
    resultLabel: { fontSize: 13.5, fontWeight: 600 },
    resultDomain: { fontSize: 11.5, color: '#9A90AE', marginTop: 2 },
};
